import {
  API_CENTRIFUGE_REPORT_HIGHLIGHTS_ENDPOINT,
  API_CENTRIFUGE_REPORTS_ENDPOINT,
  API_GET_CATALOG_TREE_ENDPOINT,
  createApiGetCentrifugeReportEndpoint,
  createApiGetCentrifugeReportPdfEndpoint,
} from '@/api/config';
import apiClient from '@/api/client';
import {
  ContentSkeleton,
  TableSkeletonRows,
} from '@/components/LoadingSkeleton/LoadingSkeleton';
import PageHeader from '@/components/PageHeader/PageHeader';
import { useLanguage, type Language } from '@/i18n/LanguageContext';
import type { TranslationKey } from '@/i18n/translations';
import type {
  CatalogTreeResponse,
  CentrifugeMode,
  CentrifugeReport,
  CentrifugeRole,
} from '@/models/models';
import {
  DATE_PICKER_VALUE_FORMAT,
  formatDisplayDateTime,
} from '@/utils/dateFormat';
import { dateRangeQuery } from '@/utils/dateRangeFilters';
import {
  formatFixedNumber,
  formatLocalizedNumber,
} from '@/utils/numberFormat';
import {
  centrifugeStatusLabel,
  localizeCentrifugeWarning,
} from '@/utils/centrifugeLocalization';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  NativeSelect,
  Pagination,
  Paper,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  DatePickerInput,
  type DatesRangeValue,
  type DateStringValue,
} from '@mantine/dates';
import IconDownload from '@tabler/icons-react/IconDownload';
import IconArrowDown from '@tabler/icons-react/IconArrowDown';
import IconArrowUp from '@tabler/icons-react/IconArrowUp';
import IconEye from '@tabler/icons-react/IconEye';
import IconTrash from '@tabler/icons-react/IconTrash';
import IconCalendar from '@tabler/icons-react/IconCalendar';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import classes from './CentrifugeReports.module.scss';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = ['10', '25', '50', '100'];
type SortDirection = 'asc' | 'desc';
type SortableColumn =
  | 'id'
  | 'created_at'
  | 'mode'
  | 'rig_well'
  | 'operator'
  | 'measurements'
  | 'barite_recovery'
  | 'lgs_removal'
  | 'hgs_removal'
  | 'status';

interface SortConfig {
  column: SortableColumn;
  direction: SortDirection;
}

interface RigWellFilterOption {
  value: string;
  label: string;
}

interface CentrifugeReportHighlightsResponse {
  data: Array<{ created_at: string | null }>;
}

function rigWellOptionsFromCatalog(
  catalog: CatalogTreeResponse
): RigWellFilterOption[] {
  return catalog.operators
    .flatMap((operator) => operator.rigs)
    .flatMap((rig) => rig.wells.map((well) => ({
      value: String(well.id),
      label: `${rig.name} - ${well.name}`,
    })))
    .sort((first, second) => first.label.localeCompare(second.label));
}

const PDF_READY_STATUSES = new Set([
  'CALCULATED',
  'CALCULATED_WITH_WARNINGS',
  'CALCULATED_WITHOUT_DRYING',
]);

function decimal(
  value: number | null | undefined,
  language: Language,
  maximumFractionDigits = 3
): string {
  return value === undefined || value === null
    ? '-'
    : formatLocalizedNumber(value, language, { maximumFractionDigits });
}

function fixedDecimal(
  value: number | null | undefined,
  language: Language,
  fractionDigits: number
): string {
  return value === undefined || value === null
    ? '-'
    : formatFixedNumber(value, language, fractionDigits);
}

function percent(value: number | undefined, language: Language): string {
  return value === undefined
    ? '-'
    : `${formatFixedNumber(value, language, 1)}%`;
}

function downloadName(contentDisposition: string | undefined, fallback: string): string {
  return contentDisposition?.match(/filename="?([^";]+)"?/)?.[1] || fallback;
}

function pdfAvailable(report: CentrifugeReport): boolean {
  return PDF_READY_STATUSES.has(report.status.toUpperCase());
}

function dualPercent(
  first: number | undefined,
  second: number | undefined,
  language: Language
): string {
  return `C1 ${percent(first, language)} / C2 ${percent(second, language)}`;
}

export default function CentrifugeReports() {
  const { t, language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reports, setReports] = useState<CentrifugeReport[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [createdRange, setCreatedRange] = useState<DatesRangeValue>([null, null]);
  const [selectedMode, setSelectedMode] = useState<CentrifugeMode | null>(null);
  const [selectedWellId, setSelectedWellId] = useState<string | null>(null);
  const [rigWellOptions, setRigWellOptions] = useState<RigWellFilterOption[]>([]);
  const [rigWellLoading, setRigWellLoading] = useState(false);
  const [createdDateKeys, setCreatedDateKeys] = useState<Set<string>>(new Set());
  const [dateHighlightsLoading, setDateHighlightsLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    column: 'created_at',
    direction: 'desc',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<CentrifugeReport | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [downloadingUuid, setDownloadingUuid] = useState<string | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<CentrifugeReport | null>(null);
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null);
  const selectedRunUuid = searchParams.get('run_uuid');
  const modeOptions = useMemo(() => [
    {
      value: 'barite_recovery',
      label: t('centrifuge.mode.barite_recovery'),
    },
    {
      value: 'dual_polishing',
      label: t('centrifuge.mode.dual_polishing'),
    },
  ], [t]);
  const hasActiveFilters = Boolean(
    createdRange[0] || createdRange[1] || selectedMode || selectedWellId
  );

  const loadReports = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const [createdFrom, createdTo] = dateRangeQuery(createdRange);
      const response = await apiClient.get<{ data: CentrifugeReport[]; total: number }>(
        API_CENTRIFUGE_REPORTS_ENDPOINT,
        {
          params: {
            limit: pageSize,
            offset: (page - 1) * pageSize,
            created_from: createdFrom,
            created_to: createdTo,
            mode: selectedMode || undefined,
            well_id: selectedWellId ? Number(selectedWellId) : undefined,
            sort_by: sortConfig.column,
            sort_order: sortConfig.direction,
          },
          signal,
        }
      );
      if (signal?.aborted) {
        return;
      }
      setReports(response.data.data);
      setTotal(response.data.total);
    } catch (requestError) {
      if (signal?.aborted) {
        return;
      }
      console.error('Failed to load centrifuge reports:', requestError);
      setError(t('centrifuge.loadError'));
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [createdRange, page, pageSize, selectedMode, selectedWellId, sortConfig, t]);

  useEffect(() => {
    const controller = new AbortController();
    loadReports(controller.signal);

    return () => controller.abort();
  }, [loadReports]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const loadDateHighlights = async () => {
      setDateHighlightsLoading(true);
      try {
        const [createdFrom, createdTo] = dateRangeQuery(createdRange);
        const response = await apiClient.get<CentrifugeReportHighlightsResponse>(
          API_CENTRIFUGE_REPORT_HIGHLIGHTS_ENDPOINT,
          {
            params: {
              created_from: createdFrom,
              created_to: createdTo,
              mode: selectedMode || undefined,
              well_id: selectedWellId ? Number(selectedWellId) : undefined,
            },
            signal: controller.signal,
          }
        );

        if (active) {
          setCreatedDateKeys(new Set(
            response.data.data.flatMap(({ created_at }) =>
              created_at ? [created_at.slice(0, 10)] : []
            )
          ));
        }
      } catch (requestError) {
        if (active) {
          console.error(
            'Failed to load centrifuge report dates:',
            requestError
          );
          setCreatedDateKeys(new Set());
        }
      } finally {
        if (active) {
          setDateHighlightsLoading(false);
        }
      }
    };

    loadDateHighlights();
    return () => {
      active = false;
      controller.abort();
    };
  }, [createdRange, selectedMode, selectedWellId]);

  useEffect(() => {
    let active = true;
    setRigWellLoading(true);
    apiClient.get<CatalogTreeResponse>(API_GET_CATALOG_TREE_ENDPOINT)
      .then((response) => {
        if (active) {
          setRigWellOptions(rigWellOptionsFromCatalog(response.data));
        }
      })
      .catch((requestError) => {
        console.error('Failed to load rig-well filter options:', requestError);
      })
      .finally(() => {
        if (active) {
          setRigWellLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const clearFilters = useCallback(() => {
    setCreatedRange([null, null]);
    setSelectedMode(null);
    setSelectedWellId(null);
    setPage(1);
  }, []);

  const handleSort = useCallback((column: SortableColumn) => {
    setSortConfig((current) => ({
      column,
      direction: current.column === column && current.direction === 'asc'
        ? 'desc'
        : 'asc',
    }));
    setPage(1);
  }, []);

  const SortIcon = ({ column }: { column: SortableColumn }) => {
    if (sortConfig.column !== column) return null;
    return sortConfig.direction === 'asc' ? (
      <IconArrowUp size={14} style={{ marginLeft: 4 }} />
    ) : (
      <IconArrowDown size={14} style={{ marginLeft: 4 }} />
    );
  };

  const sortableHeader = (column: SortableColumn, label: string) => (
    <Table.Th
      onClick={() => handleSort(column)}
      classNames={{ th: classes.sortedHeader }}
      aria-sort={
        sortConfig.column === column
          ? sortConfig.direction === 'asc' ? 'ascending' : 'descending'
          : 'none'
      }
    >
      <Group gap={4} wrap="nowrap">
        <Text span fw={700}>{label}</Text>
        <SortIcon column={column} />
      </Group>
    </Table.Th>
  );

  const openReport = useCallback((runUuid: string) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('run_uuid', runUuid);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    if (!selectedRunUuid) {
      setDetail(null);
      setDetailLoading(false);
      return;
    }

    let cancelled = false;
    setDetail(null);
    setDetailLoading(true);
    setError(null);

    const loadDetail = async () => {
      try {
        const response = await apiClient.get<CentrifugeReport>(
          createApiGetCentrifugeReportEndpoint(selectedRunUuid)
        );
        if (!cancelled) {
          setDetail(response.data);
        }
      } catch (requestError) {
        if (!cancelled) {
          console.error('Failed to load centrifuge report:', requestError);
          setError(t('centrifuge.loadDetailError'));
          setSearchParams((current) => {
            const next = new URLSearchParams(current);
            next.delete('run_uuid');
            return next;
          }, { replace: true });
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    };

    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [selectedRunUuid, setSearchParams, t]);

  const closeDetail = () => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('run_uuid');
      return next;
    }, { replace: true });
  };

  const downloadPdf = async (report: CentrifugeReport) => {
    setDownloadingUuid(report.run_uuid);
    try {
      const response = await apiClient.get(
        createApiGetCentrifugeReportPdfEndpoint(report.run_uuid),
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName(
        response.headers['content-disposition'],
        `centrifuge_performance_${report.report_id}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (requestError) {
      console.error('Failed to download centrifuge report:', requestError);
      setError(t('centrifuge.downloadError'));
    } finally {
      setDownloadingUuid(null);
    }
  };

  const deleteReport = async () => {
    if (!deleteCandidate || deletingUuid) return;
    const report = deleteCandidate;
    setDeletingUuid(report.run_uuid);
    setError(null);
    try {
      await apiClient.delete(
        createApiGetCentrifugeReportEndpoint(report.run_uuid)
      );
      setDeleteCandidate(null);
      if (selectedRunUuid === report.run_uuid) {
        closeDetail();
      }
      if (reports.length === 1 && page > 1) {
        setPage((current) => current - 1);
      } else {
        await loadReports();
      }
    } catch (requestError) {
      console.error('Failed to delete centrifuge report:', requestError);
      setError(t('centrifuge.deleteError'));
    } finally {
      setDeletingUuid(null);
    }
  };

  const metrics = detail?.metrics || {};
  const detailWarnings = detail?.warnings.filter(
    (warning) => warning !== detail.balance_error?.message
  ) || [];

  const statusBadge = (report: CentrifugeReport) => {
    const failed = report.status.toUpperCase() === 'INVALID_BALANCE';
    return (
      <Badge color={failed ? 'red' : report.warnings.length ? 'yellow' : 'green'} variant="light">
        {centrifugeStatusLabel(report.status, t)}
      </Badge>
    );
  };

  const getCreatedDayProps = useCallback(
    (date: DateStringValue) => ({
      className: dateHighlightsLoading
        ? classes.reportDayLoading
        : createdDateKeys.has(date)
          ? classes.reportDay
          : undefined,
      title: dateHighlightsLoading
        ? t('results.loadingDates')
        : createdDateKeys.has(date)
          ? t('results.reportsAvailable')
          : undefined,
    }),
    [createdDateKeys, dateHighlightsLoading, t]
  );

  return (
    <div className={classes.resultsSurface}>
      <PageHeader
        title={t('centrifuge.reportsTitle')}
        meta={t('centrifuge.count', { count: total })}
      />

      {error && <Alert color="red" withCloseButton onClose={() => setError(null)}>{error}</Alert>}

      <div className={classes.filterControls}>
        <DatePickerInput
          type="range"
          label={t('centrifuge.createdAt')}
          placeholder={t('centrifuge.createdPlaceholder')}
          value={createdRange}
          valueFormat={DATE_PICKER_VALUE_FORMAT}
          onChange={(value) => {
            setCreatedRange(value);
            setPage(1);
          }}
          allowSingleDateInRange
          clearable
          leftSection={<IconCalendar size="16" />}
          leftSectionPointerEvents="none"
          maxDate={new Date()}
          size="sm"
          getDayProps={getCreatedDayProps}
        />
        <Select
          label={t('centrifuge.mode')}
          placeholder={t('centrifuge.allModes')}
          data={modeOptions}
          value={selectedMode}
          onChange={(value) => {
            setSelectedMode(value as CentrifugeMode | null);
            setPage(1);
          }}
          searchable
          clearable
          comboboxProps={{ withinPortal: false }}
        />
        <Select
          label={t('nav.rigWell')}
          placeholder={t('results.allRigWells')}
          data={rigWellOptions}
          value={selectedWellId}
          onChange={(value) => {
            setSelectedWellId(value);
            setPage(1);
          }}
          rightSection={rigWellLoading ? <Loader size="xs" /> : undefined}
          disabled={rigWellLoading}
          searchable
          clearable
          limit={100}
          nothingFoundMessage={t('results.noRigWellMatches')}
          comboboxProps={{ withinPortal: false }}
        />
        <Button
          variant="subtle"
          color="green"
          onClick={clearFilters}
          disabled={!hasActiveFilters}
          className={classes.clearFiltersButton}
          h={36}
        >
          {t('results.clearFilters')}
        </Button>
      </div>

      <ScrollArea className={classes.tableContainer}>
        <Table
          striped
          highlightOnHover
          withTableBorder
          stickyHeader
          stickyHeaderOffset={-1}
          className={classes.reportsTable}
          aria-busy={loading}
          style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}
        >
          <Table.Thead>
            <Table.Tr>
              {sortableHeader('id', t('centrifuge.report'))}
              {sortableHeader('created_at', t('centrifuge.createdAt'))}
              {sortableHeader('mode', t('centrifuge.mode'))}
              {sortableHeader('rig_well', t('nav.rigWell'))}
              {sortableHeader('operator', t('results.operator'))}
              {sortableHeader('measurements', t('centrifuge.measurements'))}
              {sortableHeader('barite_recovery', t('centrifuge.bariteRecovery'))}
              {sortableHeader('lgs_removal', t('centrifuge.lgsRemoval'))}
              {sortableHeader('hgs_removal', t('centrifuge.hgsRemoval'))}
              {sortableHeader('status', t('centrifuge.status'))}
              <Table.Th classNames={{ th: classes.tableHeader }}>
                <Text span fw={700}>{t('common.actions')}</Text>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading && reports.length === 0 ? (
              <TableSkeletonRows columns={11} />
            ) : reports.length === 0 ? (
              <Table.Tr><Table.Td colSpan={11}><Text ta="center" c="dimmed" py="xl">{t('centrifuge.empty')}</Text></Table.Td></Table.Tr>
            ) : reports.map((report) => (
              <Table.Tr key={report.run_uuid}>
                <Table.Td>
                  <Button variant="subtle" color="green" size="compact-sm" onClick={() => openReport(report.run_uuid)}>
                    {report.report_id}
                  </Button>
                </Table.Td>
                <Table.Td>{formatDisplayDateTime(report.created_at)}</Table.Td>
                <Table.Td>{t(`centrifuge.mode.${report.mode || 'barite_recovery'}` as TranslationKey)}</Table.Td>
                <Table.Td>{[report.rig_name, report.well_name].filter(Boolean).join(' - ') || '-'}</Table.Td>
                <Table.Td>{report.operator || '-'}</Table.Td>
                <Table.Td>{report.measurement_ids.length}</Table.Td>
                <Table.Td>{percent(report.metrics?.barite_recovery_efficiency_percent, language)}</Table.Td>
                <Table.Td>{report.mode === 'dual_polishing'
                  ? dualPercent(
                    report.metrics?.centrifuge_1_lgs_removal_efficiency_percent,
                    report.metrics?.centrifuge_2_lgs_removal_efficiency_percent,
                    language
                  )
                  : percent(report.metrics?.lgs_removal_efficiency_percent, language)}</Table.Td>
                <Table.Td>{report.mode === 'dual_polishing'
                  ? dualPercent(
                    report.metrics?.centrifuge_1_hgs_removal_efficiency_percent,
                    report.metrics?.centrifuge_2_hgs_removal_efficiency_percent,
                    language
                  )
                  : percent(report.metrics?.hgs_removal_efficiency_percent, language)}</Table.Td>
                <Table.Td>{statusBadge(report)}</Table.Td>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    <Tooltip label={t('centrifuge.view')} withArrow>
                      <ActionIcon
                        variant="outline"
                        color="green"
                        size="lg"
                        className={classes.rowAction}
                        onClick={() => openReport(report.run_uuid)}
                      >
                        <IconEye size={17} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label={pdfAvailable(report) ? t('centrifuge.download') : t('centrifuge.pdfUnavailable')} withArrow>
                      <span>
                        <ActionIcon
                          variant="outline"
                          color="green"
                          size="lg"
                          className={classes.rowAction}
                          loading={downloadingUuid === report.run_uuid}
                          disabled={!pdfAvailable(report)}
                          onClick={() => downloadPdf(report)}
                        >
                          <IconDownload size={17} />
                        </ActionIcon>
                      </span>
                    </Tooltip>
                    <Tooltip label={t('centrifuge.delete')} withArrow>
                      <ActionIcon
                        variant="outline"
                        color="red"
                        size="lg"
                        className={classes.rowAction}
                        aria-label={t('centrifuge.delete')}
                        onClick={() => setDeleteCandidate(report)}
                      >
                        <IconTrash size={17} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      <div className={classes.paginationRow}>
        <Text size="sm" c="dimmed" className={classes.resultRange}>
          {t('centrifuge.showing', {
            from: reports.length === 0 ? 0 : (page - 1) * pageSize + 1,
            to: Math.min(page * pageSize, total),
            total,
          })}
        </Text>
        <Group gap="xs" wrap="nowrap" className={classes.rowsControl}>
          <Text size="xs" fw={700}>{t('results.rows')}</Text>
          <NativeSelect
            aria-label={t('results.rowsPerPage')}
            data={PAGE_SIZE_OPTIONS}
            value={String(pageSize)}
            onChange={(event) => {
              const value = event.currentTarget.value;
              if (value) {
                setPage(1);
                setPageSize(Number(value));
              }
            }}
            className={classes.rowsSelect}
            size="xs"
          />
        </Group>
        <Pagination
          total={Math.max(1, Math.ceil(total / pageSize))}
          value={page}
          onChange={setPage}
          className={classes.pagination}
        />
      </div>

      <Modal opened={Boolean(detail) || detailLoading} onClose={closeDetail} title={detail?.report_id || t('centrifuge.report')} size="xl" centered>
        {detailLoading && !detail ? <ContentSkeleton /> : detail && (
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Text fw={700}>{[detail.rig_name, detail.well_name].filter(Boolean).join(' - ')}</Text>
                <Text size="sm" c="dimmed">
                  {[formatDisplayDateTime(detail.created_at), detail.operator].filter(Boolean).join(' · ')}
                </Text>
                <Text size="sm" c="dimmed">
                  {t(`centrifuge.mode.${detail.mode || 'barite_recovery'}` as TranslationKey)}
                </Text>
              </div>
              {statusBadge(detail)}
            </Group>

            <SimpleGrid cols={{ base: 2, sm: detail.mode === 'dual_polishing' ? 3 : 5 }}>
              {(detail.mode === 'dual_polishing' ? [
                [t('centrifuge.centrifuge1LgsRemoval'), percent(metrics.centrifuge_1_lgs_removal_efficiency_percent, language)],
                [t('centrifuge.centrifuge1HgsRemoval'), percent(metrics.centrifuge_1_hgs_removal_efficiency_percent, language)],
                [t('centrifuge.centrifuge1DensityCut'), `${fixedDecimal(metrics.centrifuge_1_density_cut_ppg, language, 2)} ppg`],
                [t('centrifuge.centrifuge2LgsRemoval'), percent(metrics.centrifuge_2_lgs_removal_efficiency_percent, language)],
                [t('centrifuge.centrifuge2HgsRemoval'), percent(metrics.centrifuge_2_hgs_removal_efficiency_percent, language)],
                [t('centrifuge.centrifuge2DensityCut'), `${fixedDecimal(metrics.centrifuge_2_density_cut_ppg, language, 2)} ppg`],
              ] : [
                [t('centrifuge.bariteRecovery'), percent(metrics.barite_recovery_efficiency_percent, language)],
                [t('centrifuge.lgsRemoval'), percent(metrics.lgs_removal_efficiency_percent, language)],
                [t('centrifuge.hgsRemoval'), percent(metrics.hgs_removal_efficiency_percent, language)],
                [t('centrifuge.lsDensityCut'), `${fixedDecimal(metrics.ls_density_cut_ppg, language, 2)} ppg`],
                [t('centrifuge.hsDensityCut'), `${fixedDecimal(metrics.hs_density_cut_ppg, language, 2)} ppg`],
              ]).map(([label, value]) => (
                <Paper key={label} withBorder p="sm" radius="md">
                  <Text size="xs" c="dimmed">{label}</Text>
                  <Text fw={800}>{value}</Text>
                </Paper>
              ))}
            </SimpleGrid>

            <div>
              <Text fw={700} mb="xs">{t('centrifuge.measurements')}</Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                {Object.entries(detail.selected_result_ids).map(([role, id]) => (
                  <Paper key={role} withBorder p="sm" radius="md">
                    <Group justify="space-between">
                      <Text fw={700}>{t(`centrifuge.role.${role}` as TranslationKey)}</Text>
                      <Badge variant="light" color="green">#{id}</Badge>
                    </Group>
                    <Text size="sm" c="dimmed">
                      {t('centrifuge.flow')}: {
                        decimal(
                          detail.flows_gpm?.[role as keyof NonNullable<CentrifugeReport['flows_gpm']>]
                          ?? detail.input_values?.flow_rates_gpm?.[role as CentrifugeRole],
                          language
                        )
                      } gpm
                    </Text>
                  </Paper>
                ))}
              </SimpleGrid>
            </div>

            <div>
              <Text fw={700} mb="xs">{t('centrifuge.operatingConditions')}</Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                {Object.entries(detail.operating_conditions || {}).map(([stage, condition]) => (
                  <Paper key={stage} withBorder p="sm" radius="md">
                    <Text fw={700} mb={4}>{condition.label || condition.stage.toUpperCase()}</Text>
                    <Text size="sm">{t('centrifuge.bowlSpeed')}: {decimal(condition.bowl_speed_rpm?.effective, language)} rpm</Text>
                    <Text size="sm">{t('centrifuge.backdrive')}: {decimal(condition.backdrive_differential_rpm?.effective, language)} rpm</Text>
                    <Text size="sm">{t('centrifuge.bowlDiameter')}: {decimal(condition.bowl_diameter_inches?.effective, language)} in</Text>
                    <Text size="sm">{t('centrifuge.gForce')}: {fixedDecimal(condition.g_force, language, 1)}</Text>
                  </Paper>
                ))}
              </SimpleGrid>
            </div>

            {detail.balance_error && (
              <Alert color="red" title={t('centrifuge.calculationFailed')}>
                <Stack gap={4}>
                  {detail.balance_error.message && <Text size="sm">{detail.balance_error.message}</Text>}
                  {detail.balance_error.action && <Text size="sm" fw={700}>{detail.balance_error.action}</Text>}
                </Stack>
              </Alert>
            )}

            {detailWarnings.length > 0 && (
              <Alert color="yellow" title={t('centrifuge.warnings')}>
                <Stack gap={4}>
                  {detailWarnings.map((warning) => (
                    <Text size="sm" key={warning}>
                      {localizeCentrifugeWarning(warning, language, t)}
                    </Text>
                  ))}
                </Stack>
              </Alert>
            )}

            <Group justify="flex-end">
              <Button
                variant="light"
                color="red"
                leftSection={<IconTrash size={17} />}
                onClick={() => setDeleteCandidate(detail)}
              >
                {t('centrifuge.delete')}
              </Button>
              <Button variant="default" onClick={closeDetail}>{t('common.close')}</Button>
              <Tooltip label={pdfAvailable(detail) ? t('centrifuge.download') : t('centrifuge.pdfUnavailable')}>
                <span>
                  <Button
                    color="green"
                    leftSection={<IconDownload size={17} />}
                    loading={downloadingUuid === detail.run_uuid}
                    disabled={!pdfAvailable(detail)}
                    onClick={() => downloadPdf(detail)}
                  >
                    {t('centrifuge.download')}
                  </Button>
                </span>
              </Tooltip>
            </Group>
          </Stack>
        )}
      </Modal>

      <Modal
        opened={Boolean(deleteCandidate)}
        onClose={() => !deletingUuid && setDeleteCandidate(null)}
        title={t('centrifuge.deleteTitle', {
          report: deleteCandidate?.report_id || t('centrifuge.report'),
        })}
        centered
        closeOnClickOutside={!deletingUuid}
        closeOnEscape={!deletingUuid}
        withCloseButton={!deletingUuid}
      >
        <Stack gap="md">
          <Text size="sm">{t('centrifuge.deleteDescription')}</Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setDeleteCandidate(null)}
              disabled={Boolean(deletingUuid)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              color="red"
              leftSection={<IconTrash size={17} />}
              onClick={deleteReport}
              loading={Boolean(deletingUuid)}
            >
              {t('centrifuge.deleteConfirm')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
